
import React, { useMemo } from 'react';
import { JobSearchResult } from '@/types/jobSearch';
import { JobSearchResultItem } from './JobSearchResultItem';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Building2 } from 'lucide-react';
import { parseISO, isValid } from 'date-fns';

interface JobSearchResultsListProps {
  results: JobSearchResult[];
  savedJobIds: string[];
  onSaveJob: (job: JobSearchResult) => Promise<void>;
  onUnsaveJob: (jobSearchResultId: string) => Promise<void>;
  isSavingJobId: string | null;
  isUserLoggedIn: boolean;
  onMakeLead: (job: JobSearchResult) => void;
}

export const JobSearchResultsList: React.FC<JobSearchResultsListProps> = ({ 
  results, 
  savedJobIds,
  onSaveJob,
  onUnsaveJob,
  isSavingJobId,
  isUserLoggedIn,
  onMakeLead
}) => {
  const groupedByCompany = useMemo(() => {
    const groups = results.reduce((acc, job) => {
      const companyName = job.company_name || 'Company Not Specified';
      if (!acc[companyName]) {
        acc[companyName] = [];
      }
      acc[companyName].push(job);
      return acc;
    }, {} as Record<string, JobSearchResult[]>);

    // Sort jobs within each company group by posted_date
    for (const companyName in groups) {
      groups[companyName].sort((a, b) => {
        const dateA = a.posted_date ? parseISO(a.posted_date) : null;
        const dateB = b.posted_date ? parseISO(b.posted_date) : null;

        if (dateA && isValid(dateA) && dateB && isValid(dateB)) {
          return dateB.getTime() - dateA.getTime(); // Sort descending (newest first)
        }
        if (dateA && isValid(dateA)) {
          return -1; // A has a valid date, B does not, so A comes first
        }
        if (dateB && isValid(dateB)) {
          return 1; // B has a valid date, A does not, so B comes first
        }
        return 0; // Both dates are invalid or missing
      });
    }
    return groups;
  }, [results]);

  const companyNames = useMemo(() => Object.keys(groupedByCompany).sort((a,b) => groupedByCompany[b].length - groupedByCompany[a].length), [groupedByCompany]);

  if (results.length === 0) {
    return <p className="text-center text-muted-foreground mt-8">No jobs found. Try adjusting your search criteria.</p>;
  }

  // Determine default open accordion item (e.g., first company)
  const defaultOpenValue = companyNames.length > 0 ? companyNames[0] : undefined;

  return (
    <div className="mt-6">
      <h2 className="text-2xl font-semibold mb-6">Search Results ({results.length} jobs found)</h2>
      <Accordion type="single" collapsible className="w-full space-y-4" defaultValue={defaultOpenValue}>
        {companyNames.map((companyName) => (
          <AccordionItem value={companyName} key={companyName} className="border rounded-lg overflow-hidden">
            <AccordionTrigger className="px-6 py-4 hover:bg-muted/50 transition-colors [&[data-state=open]>svg]:rotate-180">
              <div className="flex items-center space-x-3">
                <Building2 className="h-5 w-5 text-primary" />
                <span className="font-semibold text-lg">{companyName}</span>
                <span className="text-sm text-muted-foreground">({groupedByCompany[companyName].length} job{groupedByCompany[companyName].length === 1 ? '' : 's'})</span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-6 pt-0 pb-4 border-t bg-muted/20">
              <div className="space-y-4 pt-4">
                {groupedByCompany[companyName].map((job) => (
                  <JobSearchResultItem
                    key={job.id}
                    job={job}
                    isSaved={savedJobIds.includes(job.id)}
                    onSaveJob={() => onSaveJob(job)}
                    onUnsaveJob={() => onUnsaveJob(job.id)}
                    isSavingThisJob={isSavingJobId === job.id}
                    isUserLoggedIn={isUserLoggedIn}
                    onMakeLead={onMakeLead}
                  />
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
};

